import { useState, useEffect } from 'react';
import { 
  Package, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  CheckCircle, 
  AlertTriangle,
  X
} from 'lucide-react';

interface Subscription {
  id: string;
  name: string;
  price_monthly: number;
  max_weight_kg: number;
  shipping_discount: number;
  handling_discount: number;
  benefits: string[];
  limitations: string[];
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

const Assinaturas = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewSubscription, setShowNewSubscription] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<string | null>(null);
  const [newSubscription, setNewSubscription] = useState<Partial<Subscription>>({
    name: '',
    price_monthly: 0,
    max_weight_kg: 0,
    shipping_discount: 0,
    handling_discount: 0,
    benefits: [],
    limitations: [],
    active: true
  });
  const [newBenefit, setNewBenefit] = useState('');
  const [newLimitation, setNewLimitation] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const authHeader: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch('/api/subscriptions', { headers: authHeader });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.data || []);
      } else {
        console.error('Failed to load subscriptions:', await response.text());
        // Load sample data for demo
        const sampleSubscriptions: Subscription[] = [
          {
            id: '1',
            name: 'Just One',
            price_monthly: 1.99,
            max_weight_kg: 3.0,
            shipping_discount: 10.0,
            handling_discount: 15.0,
            benefits: [
              'Caixa com até 3kg de produtos',
              'Desconto de $10 no frete',
              'Desconto de $15 no handling',
              'Ideal para compras pequenas'
            ],
            limitations: [
              'Máximo 3kg por envio',
              'Apenas 1 caixa por mês',
              'Não acumula com outras promoções'
            ],
            active: true
          },
          {
            id: '2',
            name: '5 por $55',
            price_monthly: 5.99,
            max_weight_kg: 5.0,
            shipping_discount: 15.0,
            handling_discount: 40.0,
            benefits: [
              'Caixa com até 5kg de produtos',
              'Desconto de $15 no frete',
              'Desconto de $40 no handling',
              'Até 5 caixas por mês',
              'Melhor custo-benefício'
            ],
            limitations: [
              'Máximo 5kg por envio',
              'Válido apenas para produtos elegíveis',
              'Sujeito a disponibilidade'
            ],
            active: true
          },
          {
            id: '3',
            name: 'Premium Plus',
            price_monthly: 12.99,
            max_weight_kg: 10.0,
            shipping_discount: 25.0,
            handling_discount: 60.0,
            benefits: [
              'Caixa com até 10kg de produtos',
              'Desconto de $25 no frete',
              'Desconto de $60 no handling',
              'Envios ilimitados',
              'Suporte prioritário',
              'Produtos exclusivos'
            ],
            limitations: [
              'Máximo 10kg por envio',
              'Compromisso mínimo de 3 meses'
            ],
            active: true
          }
        ];
        setSubscriptions(sampleSubscriptions);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!newSubscription.name || !newSubscription.price_monthly || !newSubscription.max_weight_kg) {
      alert('Nome, preço mensal e peso máximo são obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const authHeader: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify(newSubscription)
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptions([...subscriptions, data.data]);
        setShowNewSubscription(false);
        resetNewSubscription();
      } else {
        console.error('Failed to create subscription:', await response.text());
        
        // For demo, add to local state
        const newSub: Subscription = {
          id: Date.now().toString(),
          name: newSubscription.name || '',
          price_monthly: newSubscription.price_monthly || 0,
          max_weight_kg: newSubscription.max_weight_kg || 0,
          shipping_discount: newSubscription.shipping_discount || 0,
          handling_discount: newSubscription.handling_discount || 0,
          benefits: newSubscription.benefits || [],
          limitations: newSubscription.limitations || [],
          active: newSubscription.active !== undefined ? newSubscription.active : true
        };
        
        setSubscriptions([...subscriptions, newSub]);
        setShowNewSubscription(false);
        resetNewSubscription();
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert('Erro ao criar assinatura');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSubscription = async (id: string) => {
    if (!newSubscription.name || !newSubscription.price_monthly || !newSubscription.max_weight_kg) {
      alert('Nome, preço mensal e peso máximo são obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const authHeader: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`/api/subscriptions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify(newSubscription)
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptions(subscriptions.map(sub => sub.id === id ? data.data : sub));
        setEditingSubscription(null);
        resetNewSubscription();
      } else {
        console.error('Failed to update subscription:', await response.text());
        
        // For demo, update local state
        setSubscriptions(subscriptions.map(sub => 
          sub.id === id ? { ...sub, ...newSubscription } : sub
        ));
        setEditingSubscription(null);
        resetNewSubscription();
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('Erro ao atualizar assinatura');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubscription = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta assinatura?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const authHeader: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`/api/subscriptions/${id}`, {
        method: 'DELETE',
        headers: authHeader
      });

      if (response.ok) {
        setSubscriptions(subscriptions.filter(sub => sub.id !== id));
      } else {
        console.error('Failed to delete subscription:', await response.text());
        
        // For demo, remove from local state
        setSubscriptions(subscriptions.filter(sub => sub.id !== id));
      }
    } catch (error) {
      console.error('Error deleting subscription:', error);
      alert('Erro ao excluir assinatura');
    }
  };

  const resetNewSubscription = () => {
    setNewSubscription({
      name: '',
      price_monthly: 0,
      max_weight_kg: 0,
      shipping_discount: 0,
      handling_discount: 0,
      benefits: [],
      limitations: [],
      active: true
    });
    setNewBenefit('');
    setNewLimitation('');
  };

  const addBenefit = () => {
    if (!newBenefit.trim()) return;
    setNewSubscription({
      ...newSubscription,
      benefits: [...(newSubscription.benefits || []), newBenefit]
    });
    setNewBenefit('');
  };

  const removeBenefit = (index: number) => {
    const benefits = [...(newSubscription.benefits || [])];
    benefits.splice(index, 1);
    setNewSubscription({
      ...newSubscription,
      benefits
    });
  };

  const addLimitation = () => {
    if (!newLimitation.trim()) return;
    setNewSubscription({
      ...newSubscription,
      limitations: [...(newSubscription.limitations || []), newLimitation]
    });
    setNewLimitation('');
  };

  const removeLimitation = (index: number) => {
    const limitations = [...(newSubscription.limitations || [])];
    limitations.splice(index, 1);
    setNewSubscription({
      ...newSubscription,
      limitations
    });
  };

  const startEditing = (subscription: Subscription) => {
    setEditingSubscription(subscription.id);
    setNewSubscription({
      name: subscription.name,
      price_monthly: subscription.price_monthly,
      max_weight_kg: subscription.max_weight_kg,
      shipping_discount: subscription.shipping_discount,
      handling_discount: subscription.handling_discount,
      benefits: [...subscription.benefits],
      limitations: [...subscription.limitations],
      active: subscription.active
    });
  };

  const filteredSubscriptions = subscriptions.filter(subscription =>
    subscription.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planos de Assinatura</h1>
          <p className="text-gray-600 mt-1">Gerencie os planos de assinatura disponíveis</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar assinaturas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => {
              resetNewSubscription();
              setShowNewSubscription(true);
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Assinatura
          </button>
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Package className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-blue-800">Sobre os Planos de Assinatura</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p className="mb-2">
                Os planos de assinatura permitem oferecer benefícios especiais aos clientes, como:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Taxas fixas de frete e handling</li>
                <li>Limites de peso por envio</li>
                <li>Benefícios exclusivos</li>
              </ul>
              <p className="mt-2">
                <strong>Importante:</strong> Ao configurar um plano, defina valores fixos para frete e handling que serão aplicados quando o cliente tiver a assinatura.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* New Subscription Form */}
      {showNewSubscription && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Novo Plano de Assinatura</h3>
            <button
              onClick={() => setShowNewSubscription(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Plano
              </label>
              <input
                type="text"
                value={newSubscription.name}
                onChange={(e) => setNewSubscription({...newSubscription, name: e.target.value})}
                placeholder="Ex: Plano Básico"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preço Mensal ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newSubscription.price_monthly}
                onChange={(e) => setNewSubscription({...newSubscription, price_monthly: parseFloat(e.target.value)})}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Peso Máximo (kg)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={newSubscription.max_weight_kg}
                onChange={(e) => setNewSubscription({...newSubscription, max_weight_kg: parseFloat(e.target.value)})}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Taxa Fixa de Frete ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newSubscription.shipping_discount}
                onChange={(e) => setNewSubscription({...newSubscription, shipping_discount: parseFloat(e.target.value)})}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Valor fixo de frete que será cobrado quando o cliente tiver esta assinatura
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Taxa Fixa de Handling ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newSubscription.handling_discount}
                onChange={(e) => setNewSubscription({...newSubscription, handling_discount: parseFloat(e.target.value)})}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Valor fixo de handling que será cobrado quando o cliente tiver esta assinatura
              </p>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="active"
                checked={newSubscription.active}
                onChange={(e) => setNewSubscription({...newSubscription, active: e.target.checked})}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                Plano Ativo
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Benefícios
              </label>
              <div className="flex items-center mb-2">
                <input
                  type="text"
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  placeholder="Ex: Frete com taxa fixa"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addBenefit();
                    }
                  }}
                />
                <button
                  onClick={addBenefit}
                  className="px-3 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-2 mt-2">
                {newSubscription.benefits?.map((benefit, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-sm">{benefit}</span>
                    </div>
                    <button
                      onClick={() => removeBenefit(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Limitações
              </label>
              <div className="flex items-center mb-2">
                <input
                  type="text"
                  value={newLimitation}
                  onChange={(e) => setNewLimitation(e.target.value)}
                  placeholder="Ex: Máximo 5kg por envio"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addLimitation();
                    }
                  }}
                />
                <button
                  onClick={addLimitation}
                  className="px-3 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-2 mt-2">
                {newSubscription.limitations?.map((limitation, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                    <div className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-orange-500 mr-2" />
                      <span className="text-sm">{limitation}</span>
                    </div>
                    <button
                      onClick={() => removeLimitation(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={() => setShowNewSubscription(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 mr-2 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateSubscription}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <span className="animate-spin inline-block h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full"></span>
                  Salvando...
                </>
              ) : (
                'Criar Assinatura'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Subscriptions List */}
      <div className="space-y-4">
        {filteredSubscriptions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma assinatura encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Tente ajustar sua busca' : 'Comece criando seu primeiro plano de assinatura'}
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  resetNewSubscription();
                  setShowNewSubscription(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Assinatura
              </button>
            </div>
          </div>
        ) : (
          filteredSubscriptions.map((subscription) => (
            <div key={subscription.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {editingSubscription === subscription.id ? (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Editar Assinatura</h3>
                    <button
                      onClick={() => setEditingSubscription(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome do Plano
                      </label>
                      <input
                        type="text"
                        value={newSubscription.name}
                        onChange={(e) => setNewSubscription({...newSubscription, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preço Mensal ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newSubscription.price_monthly}
                        onChange={(e) => setNewSubscription({...newSubscription, price_monthly: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Peso Máximo (kg)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={newSubscription.max_weight_kg}
                        onChange={(e) => setNewSubscription({...newSubscription, max_weight_kg: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Taxa Fixa de Frete ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newSubscription.shipping_discount}
                        onChange={(e) => setNewSubscription({...newSubscription, shipping_discount: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Valor fixo de frete que será cobrado quando o cliente tiver esta assinatura
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Taxa Fixa de Handling ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newSubscription.handling_discount}
                        onChange={(e) => setNewSubscription({...newSubscription, handling_discount: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Valor fixo de handling que será cobrado quando o cliente tiver esta assinatura
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`active-${subscription.id}`}
                        checked={newSubscription.active}
                        onChange={(e) => setNewSubscription({...newSubscription, active: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`active-${subscription.id}`} className="ml-2 block text-sm text-gray-900">
                        Plano Ativo
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Benefícios
                      </label>
                      <div className="flex items-center mb-2">
                        <input
                          type="text"
                          value={newBenefit}
                          onChange={(e) => setNewBenefit(e.target.value)}
                          placeholder="Ex: Frete com taxa fixa"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addBenefit();
                            }
                          }}
                        />
                        <button
                          onClick={addBenefit}
                          className="px-3 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="space-y-2 mt-2">
                        {newSubscription.benefits?.map((benefit, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                            <div className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              <span className="text-sm">{benefit}</span>
                            </div>
                            <button
                              onClick={() => removeBenefit(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Limitações
                      </label>
                      <div className="flex items-center mb-2">
                        <input
                          type="text"
                          value={newLimitation}
                          onChange={(e) => setNewLimitation(e.target.value)}
                          placeholder="Ex: Máximo 5kg por envio"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addLimitation();
                            }
                          }}
                        />
                        <button
                          onClick={addLimitation}
                          className="px-3 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="space-y-2 mt-2">
                        {newSubscription.limitations?.map((limitation, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                            <div className="flex items-center">
                              <AlertTriangle className="h-4 w-4 text-orange-500 mr-2" />
                              <span className="text-sm">{limitation}</span>
                            </div>
                            <button
                              onClick={() => removeLimitation(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <button
                      onClick={() => setEditingSubscription(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 mr-2 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => handleUpdateSubscription(subscription.id)}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? (
                        <>
                          <span className="animate-spin inline-block h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full"></span>
                          Salvando...
                        </>
                      ) : (
                        'Salvar Alterações'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className={`p-3 rounded-full ${subscription.active ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Package className={`h-6 w-6 ${subscription.active ? 'text-green-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <h3 className="text-lg font-semibold text-gray-900">{subscription.name}</h3>
                          <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                            subscription.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {subscription.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          ${subscription.price_monthly.toFixed(2)}/mês • Máx {subscription.max_weight_kg}kg
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => startEditing(subscription)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteSubscription(subscription.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Detalhes do Plano</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Preço Mensal:</span>
                            <span className="text-sm font-medium text-gray-900">${subscription.price_monthly.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Peso Máximo:</span>
                            <span className="text-sm font-medium text-gray-900">{subscription.max_weight_kg}kg</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Taxa Fixa de Frete:</span>
                            <span className="text-sm font-medium text-gray-900">${subscription.shipping_discount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Taxa Fixa de Handling:</span>
                            <span className="text-sm font-medium text-gray-900">${subscription.handling_discount.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Benefícios</h4>
                        <ul className="space-y-2">
                          {subscription.benefits.map((benefit, index) => (
                            <li key={index} className="flex items-start">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
                              <span className="text-sm text-gray-700">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Limitações</h4>
                        <ul className="space-y-2">
                          {subscription.limitations.map((limitation, index) => (
                            <li key={index} className="flex items-start">
                              <AlertTriangle className="h-4 w-4 text-orange-500 mr-2 mt-0.5" />
                              <span className="text-sm text-gray-700">{limitation}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Assinaturas;